// ui.js
// Displays the drag-and-drop UI
// --------------------------------------------------

import { useState, useRef, useCallback } from 'react';
import { ReactFlow, Controls, Background, MiniMap } from '@xyflow/react';
import { useStore } from './store';
import { shallow } from 'zustand/shallow';
import { InputNode } from './nodes/inputNode';
import { LLMNode } from './nodes/llmNode';
import { OutputNode } from './nodes/outputNode';
import { TextNode } from './nodes/textNode';
import { DatabaseNode } from './nodes/databaseNode';
import { NoteNode } from './nodes/noteNode';
import { LogicNode } from './nodes/logicNode';
import { IntegrationNode } from './nodes/integrationNode';
import { TextFileNode } from './nodes/textFileNode';

import '@xyflow/react/dist/style.css';

const gridSize = 20;
const proOptions = { hideAttribution: true };
const nodeTypes = {
  customInput: InputNode,
  llm: LLMNode,
  customOutput: OutputNode,
  text: TextNode,
  database: DatabaseNode,
  note: NoteNode,
  logic: LogicNode,
  integration: IntegrationNode,
  textFile: TextFileNode,
};

const selector = (state) => ({
  nodes: state.nodes,
  edges: state.edges,
  getNodeID: state.getNodeID,
  addNode: state.addNode,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
  onConnect: state.onConnect,
  onEdgesDelete: state.onEdgesDelete,
});

export const PipelineUI = () => {
    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const {
      nodes,
      edges,
      getNodeID,
      addNode,
      onNodesChange,
      onEdgesChange,
      onConnect,
      onEdgesDelete
    } = useStore(selector, shallow);

    const getInitNodeData = (nodeID, type) => {
      let nodeData = { id: nodeID, nodeType: `${type}` };
      return nodeData;
    }

    const onDrop = useCallback(
        (event) => {
          event.preventDefault();
    
          if (event?.dataTransfer?.getData('application/reactflow')) {
            const appData = JSON.parse(event.dataTransfer.getData('application/reactflow'));
            const type = appData?.nodeType;
      
            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
              return;
            }
      
            const position = reactFlowInstance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });

            const nodeID = getNodeID(type);
            const newNode = {
              id: nodeID,
              type,
              position,
              data: getInitNodeData(nodeID, type),
            };
      
            addNode(newNode);
          }
        },
        [reactFlowInstance, getNodeID, addNode]
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    return (
        <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onDelete={({ edges }) => onEdgesDelete(edges)}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                proOptions={proOptions}
                snapToGrid
                snapGrid={[gridSize, gridSize]}
                connectionLineType="smoothstep"
            >
                <Background color="#cbd5e1" gap={gridSize} variant="dots" />
                <Controls />
                <MiniMap 
                    nodeStrokeColor={(n) => {
                        if (n.type === 'customInput') return '#6366f1';
                        if (n.type === 'llm') return '#10b981';
                        return '#64748b';
                    }}
                    nodeColor={(n) => {
                        return '#fff';
                    }}
                />
            </ReactFlow>
            
            {nodes.length === 0 && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    textAlign: 'center',
                    pointerEvents: 'none',
                    opacity: 0.5
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖱️</div>
                    <h3 style={{ color: '#64748b', fontWeight: 500 }}>
                        Drag a node from the toolbar to start your pipeline
                    </h3>
                </div>
            )}
        </div>
    )
}
